/**
 * Pure text chunker for incremental TTS: splits a streaming assistant answer
 * into speakable pieces, each carrying [start, end) character offsets into
 * the full message text (highlighting depends on these — strings alone are
 * not enough). No React, no I/O — safe to unit test directly.
 *
 * Two entry points:
 *   chunk(text, opts)        — one-shot, for a complete string.
 *   new ProgressiveChunker() — streaming: push(delta) returns any chunks that
 *                              just became complete; flush() finalizes
 *                              whatever's left buffered at end-of-stream.
 */

export interface TextChunk {
  text: string;
  start: number;
  end: number;
}

export interface ChunkerOptions {
  firstChunkMaxChars?: number;
  chunkMinChars?: number;
  chunkHardMaxChars?: number;
  sentenceBoundary?: RegExp;
}

interface ResolvedOptions {
  firstChunkMaxChars: number;
  chunkMinChars: number;
  chunkHardMaxChars: number;
  sentenceBoundary: RegExp;
}

interface Sentence {
  text: string;
  start: number;
  end: number;
}

export const DEFAULT_SENTENCE_BOUNDARY = /[.!?।॥]+[\s]*/;

const DEFAULTS: ResolvedOptions = {
  firstChunkMaxChars: 30,
  chunkMinChars: 60,
  chunkHardMaxChars: 2500,
  sentenceBoundary: DEFAULT_SENTENCE_BOUNDARY,
};

function resolveOptions(opts?: ChunkerOptions): ResolvedOptions {
  return {
    firstChunkMaxChars: opts?.firstChunkMaxChars ?? DEFAULTS.firstChunkMaxChars,
    chunkMinChars: opts?.chunkMinChars ?? DEFAULTS.chunkMinChars,
    chunkHardMaxChars: opts?.chunkHardMaxChars ?? DEFAULTS.chunkHardMaxChars,
    sentenceBoundary: opts?.sentenceBoundary ?? DEFAULTS.sentenceBoundary,
  };
}

// Nearest word boundary at-or-before `limit` within `text` (both indices
// relative to `text`). Returns the index right after the whitespace run
// preceding the word that straddles `limit`, so a cut there never lands
// inside a word. Falls back to `limit` itself only if `text` has no
// whitespace at all before it (one word longer than the hard cap) — an
// unavoidable hard cut, since "never truncate" can't apply to a single
// unbreakable token.
function wordBoundaryBefore(text: string, limit: number): number {
  const cap = Math.min(limit, text.length);
  if (cap >= text.length) return text.length;
  if (/\s/.test(text[cap])) return cap;
  let i = cap;
  while (i > 0 && !/\s/.test(text[i - 1])) i--;
  return i > 0 ? i : cap;
}

// Splits one over-hard-max span into pieces <= hardMax, cut at word
// boundaries, offsets relative to the full original text via `textStart`.
function splitOversized(text: string, textStart: number, hardMax: number): TextChunk[] {
  const pieces: TextChunk[] = [];
  let offset = 0;
  while (text.length - offset > hardMax) {
    const relCut = wordBoundaryBefore(text.slice(offset), hardMax);
    const cut = relCut > 0 ? relCut : hardMax;
    pieces.push({
      text: text.slice(offset, offset + cut),
      start: textStart + offset,
      end: textStart + offset + cut,
    });
    offset += cut;
    while (offset < text.length && /\s/.test(text[offset])) offset++;
  }
  if (offset < text.length) {
    pieces.push({
      text: text.slice(offset),
      start: textStart + offset,
      end: textStart + text.length,
    });
  }
  return pieces;
}

// Splits `text` into terminated sentences (each ending at a sentence-boundary
// match) plus whatever trailing text has no terminator yet ("remainder").
// Offsets are relative to `text` itself; callers shift them as needed.
function splitIntoSentences(
  text: string,
  boundary: RegExp
): { sentences: Sentence[]; remainder: Sentence | null } {
  const flags = boundary.flags.includes("g") ? boundary.flags : boundary.flags + "g";
  const re = new RegExp(boundary.source, flags);
  const sentences: Sentence[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const end = match.index + match[0].length;
    if (end > cursor) sentences.push({ text: text.slice(cursor, end), start: cursor, end });
    cursor = end;
    if (match[0].length === 0) re.lastIndex++;
  }
  const remainder =
    cursor < text.length ? { text: text.slice(cursor), start: cursor, end: text.length } : null;
  return { sentences, remainder };
}

interface EmitResult {
  chunks: TextChunk[];
  firstChunkEmitted: boolean;
  leftoverSentences: Sentence[];
}

// Core grouping engine, shared by chunk() and ProgressiveChunker. `sentences`
// are newly-available terminated sentences this call; `carry` is whatever was
// left buffered from a previous call (progressive use only — always [] for
// the one-shot path). `isFinal` forces everything still buffered out as a
// last chunk (there's no more text coming).
function emitChunks(
  sentences: Sentence[],
  carry: Sentence[],
  opts: ResolvedOptions,
  firstChunkEmittedIn: boolean,
  isFinal: boolean
): EmitResult {
  const chunks: TextChunk[] = [];
  let pending: Sentence[] = [...carry];
  let pendingLen = pending.reduce((n, s) => n + s.text.length, 0);
  let firstChunkEmitted = firstChunkEmittedIn;

  const flushPending = () => {
    if (pending.length === 0) return;
    const start = pending[0].start;
    const end = pending[pending.length - 1].end;
    const text = pending.map((s) => s.text).join("");
    if (text.length > opts.chunkHardMaxChars) {
      chunks.push(...splitOversized(text, start, opts.chunkHardMaxChars));
    } else {
      chunks.push({ text, start, end });
    }
    pending = [];
    pendingLen = 0;
  };

  for (const s of sentences) {
    if (!firstChunkEmitted) {
      flushPending(); // carry (shouldn't normally hold anything pre-first-chunk, but be safe)
      if (s.text.length <= opts.firstChunkMaxChars) {
        chunks.push({ text: s.text, start: s.start, end: s.end });
      } else {
        const cut = wordBoundaryBefore(s.text, opts.firstChunkMaxChars);
        const cutLen = cut > 0 ? cut : opts.firstChunkMaxChars;
        chunks.push({ text: s.text.slice(0, cutLen), start: s.start, end: s.start + cutLen });
        const tail = s.text.slice(cutLen);
        const strippedLen = tail.length - tail.replace(/^\s+/, "").length;
        const rem = tail.slice(strippedLen);
        if (rem.length > 0) {
          pending.push({ text: rem, start: s.start + cutLen + strippedLen, end: s.end });
          pendingLen += rem.length;
        }
      }
      firstChunkEmitted = true;
      continue;
    }

    if (s.text.length > opts.chunkHardMaxChars) {
      flushPending();
      chunks.push(...splitOversized(s.text, s.start, opts.chunkHardMaxChars));
      continue;
    }

    pending.push(s);
    pendingLen += s.text.length;
    if (pendingLen >= opts.chunkMinChars) flushPending();
  }

  if (isFinal) {
    flushPending();
    return { chunks, firstChunkEmitted, leftoverSentences: [] };
  }
  return { chunks, firstChunkEmitted, leftoverSentences: pending };
}

/** One-shot: chunk a complete string. */
export function chunk(text: string, opts?: ChunkerOptions): TextChunk[] {
  if (!text || text.length === 0) return [];
  const resolved = resolveOptions(opts);
  const { sentences, remainder } = splitIntoSentences(text, resolved.sentenceBoundary);
  const all = remainder ? [...sentences, remainder] : sentences;
  const { chunks } = emitChunks(all, [], resolved, false, true);
  return chunks;
}

/** Streaming: push text deltas as they arrive, pull complete chunks back. */
export class ProgressiveChunker {
  private pendingText = "";
  private offsetBase = 0;
  private firstChunkEmitted = false;
  private carry: Sentence[] = [];
  private opts: ResolvedOptions;

  constructor(opts?: ChunkerOptions) {
    this.opts = resolveOptions(opts);
  }

  /** Feed the next delta; returns any chunks that just became complete. */
  push(delta: string): TextChunk[] {
    this.pendingText += delta;
    const { sentences, remainder } = splitIntoSentences(this.pendingText, this.opts.sentenceBoundary);
    const shifted = sentences.map((s) => ({
      text: s.text,
      start: s.start + this.offsetBase,
      end: s.end + this.offsetBase,
    }));

    const result = emitChunks(shifted, this.carry, this.opts, this.firstChunkEmitted, false);
    this.firstChunkEmitted = result.firstChunkEmitted;
    this.carry = result.leftoverSentences;

    if (remainder) {
      this.offsetBase += remainder.start;
      this.pendingText = remainder.text;
    } else {
      this.offsetBase += this.pendingText.length;
      this.pendingText = "";
    }

    return result.chunks;
  }

  /** End of stream: flush whatever's still buffered as final chunk(s). */
  flush(): TextChunk[] {
    const finalTail: Sentence[] =
      this.pendingText.length > 0
        ? [{ text: this.pendingText, start: this.offsetBase, end: this.offsetBase + this.pendingText.length }]
        : [];
    const carryPlusTail = [...this.carry, ...finalTail];
    this.carry = [];
    this.pendingText = "";
    if (carryPlusTail.length === 0) return [];
    const result = emitChunks([], carryPlusTail, this.opts, this.firstChunkEmitted, true);
    this.firstChunkEmitted = result.firstChunkEmitted;
    return result.chunks;
  }
}
